import * as XLSX from "xlsx";

const TOTAL_CODES = ["P", "A", "L", "H", "HD", "HP", "WO"];

const LEGEND = [
  { code: "P", label: "Present" },
  { code: "A", label: "Absent" },
  { code: "L", label: "Leave" },
  { code: "H", label: "Holiday" },
  { code: "HD", label: "Half Day" },
  { code: "HP", label: "Holiday Present" },
  { code: "WO", label: "Week Off" },
];

function statusByDate(row) {
  const map = {};
  (row.day_statuses || []).forEach((ds) => {
    map[ds.date] = ds.status;
  });
  return map;
}

function buildAoA({
  organizationName = "",
  periodLabel = "",
  generatedOn = "",
  days = [],
  rows = [],
}) {
  const aoa = [];

  // Compact header — no blank spacer rows between title and table
  aoa.push([organizationName || "Organization"]);
  aoa.push(["Monthly Basic Attendance Report"]);

  const metaRow = [periodLabel || ""];
  if (generatedOn) {
    metaRow.push(`Generated On: ${generatedOn}`);
  }
  aoa.push(metaRow);

  aoa.push([
    "Legend:",
    ...LEGEND.map((item) => `${item.code}=${item.label}`),
  ]);

  const dateHeader = ["No", "Employee Code", "Employee Name"];
  days.forEach((d) => dateHeader.push(d.label));
  TOTAL_CODES.forEach((code) => dateHeader.push(code));
  aoa.push(dateHeader);

  const weekdayHeader = ["", "", ""];
  days.forEach((d) => weekdayHeader.push(d.weekday || ""));
  TOTAL_CODES.forEach(() => weekdayHeader.push(""));
  aoa.push(weekdayHeader);

  rows.forEach((row, index) => {
    const byDate = statusByDate(row);
    const totals = row.matrix_totals || {};
    const line = [
      index + 1,
      row.person_code || "",
      row.full_name || "",
    ];
    days.forEach((d) => line.push(byDate[d.date] || "-"));
    TOTAL_CODES.forEach((code) => line.push(totals[code] ?? 0));
    aoa.push(line);
  });

  return aoa;
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function isMonthlyBasicExportPayload(payload) {
  return Boolean(payload && Array.isArray(payload.days) && Array.isArray(payload.rows));
}

export function exportMonthlyBasicToExcel(payload) {
  const aoa = buildAoA(payload);
  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Compact default row heights so Excel does not stretch the header area
  ws["!rows"] = aoa.map(() => ({ hpt: 16 }));

  const colCount = aoa.reduce((max, row) => Math.max(max, row.length), 0);
  // Merge title rows across columns so they read as a report header, without blank rows
  if (colCount > 1) {
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    ];
  }

  ws["!cols"] = Array.from({ length: colCount }, (_, i) => {
    let maxLen = 8;
    aoa.forEach((row) => {
      const len = String(row[i] ?? "").length;
      maxLen = Math.max(maxLen, Math.min(len + 2, i < 3 ? 24 : 10));
    });
    return { wch: maxLen };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Monthly Basic");
  const excelBuffer = XLSX.write(wb, { bookType: "xls", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/vnd.ms-excel" });
  downloadBlob(
    blob,
    `${payload.filename || "Monthly_Basic_Attendance_Report"}.xls`
  );
}

export function exportMonthlyBasicToCSV(payload) {
  const aoa = buildAoA(payload);
  const csv = aoa
    .map((row) =>
      row
        .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
        .join(",")
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  downloadBlob(
    blob,
    `${payload.filename || "Monthly_Basic_Attendance_Report"}.csv`
  );
}

export function exportMonthlyBasicToHTML(payload) {
  const {
    organizationName = "",
    periodLabel = "",
    generatedOn = "",
    days = [],
    rows = [],
  } = payload;

  const legendHtml = LEGEND.map(
    (item) =>
      `<span style="margin-right:10px;"><strong>${item.code}</strong> ${item.label}</span>`
  ).join("");

  const dateCells = days
    .map((d) => `<th style="border:1px solid #ccc;padding:4px;">${d.label}</th>`)
    .join("");
  const weekdayCells = days
    .map(
      (d) =>
        `<th style="border:1px solid #ccc;padding:4px;">${d.weekday || ""}</th>`
    )
    .join("");
  const totalHeads = TOTAL_CODES.map(
    (c) =>
      `<th rowspan="2" style="border:1px solid #ccc;padding:4px;">${c}</th>`
  ).join("");

  const bodyRows = rows
    .map((row, index) => {
      const byDate = statusByDate(row);
      const totals = row.matrix_totals || {};
      const dayCells = days
        .map(
          (d) =>
            `<td style="border:1px solid #ccc;padding:4px;text-align:center;">${
              byDate[d.date] || "-"
            }</td>`
        )
        .join("");
      const totalCells = TOTAL_CODES.map(
        (c) =>
          `<td style="border:1px solid #ccc;padding:4px;text-align:center;">${
            totals[c] ?? 0
          }</td>`
      ).join("");
      return `<tr>
        <td style="border:1px solid #ccc;padding:4px;">${index + 1}</td>
        <td style="border:1px solid #ccc;padding:4px;">${row.person_code || ""}</td>
        <td style="border:1px solid #ccc;padding:4px;">${row.full_name || ""}</td>
        ${dayCells}${totalCells}
      </tr>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Monthly Basic Attendance Report</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 12px; }
    h1,h2,h3 { text-align: center; margin: 2px 0; }
    .meta { text-align: center; color: #555; margin: 2px 0 6px; }
    .generated { text-align: right; color: #555; font-size: 12px; margin: 0 0 6px; }
    table { border-collapse: collapse; width: max-content; min-width: 100%; font-size: 12px; margin-top: 4px; }
  </style>
</head>
<body>
  <h2>${organizationName || "Organization"}</h2>
  <h3>Monthly Basic Attendance Report</h3>
  <div class="meta">${periodLabel || ""}</div>
  ${generatedOn ? `<div class="generated">Generated On: ${generatedOn}</div>` : ""}
  <div style="margin:4px 0 6px;">${legendHtml}</div>
  <table>
    <thead>
      <tr>
        <th rowspan="2" style="border:1px solid #ccc;padding:4px;">No</th>
        <th rowspan="2" style="border:1px solid #ccc;padding:4px;">Employee Code</th>
        <th rowspan="2" style="border:1px solid #ccc;padding:4px;">Employee Name</th>
        ${dateCells}${totalHeads}
      </tr>
      <tr>${weekdayCells}</tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;

  const blob = new Blob([html], { type: "text/html" });
  downloadBlob(
    blob,
    `${payload.filename || "Monthly_Basic_Attendance_Report"}.html`
  );
}

export async function exportMonthlyBasicToPDF(payload) {
  const jsPDFModule = await import("jspdf");
  const jsPDF = jsPDFModule.default;
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const {
    organizationName = "",
    periodLabel = "",
    generatedOn = "",
    days = [],
    rows = [],
  } = payload;

  const margin = 24;
  let y = margin;
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFontSize(14);
  doc.text(organizationName || "Organization", pageWidth / 2, y, {
    align: "center",
  });
  y += 18;
  doc.setFontSize(12);
  doc.text("Monthly Basic Attendance Report", pageWidth / 2, y, {
    align: "center",
  });
  y += 14;
  doc.setFontSize(9);
  if (periodLabel) {
    doc.text(periodLabel, pageWidth / 2, y, { align: "center" });
    y += 12;
  }
  y += 4;
  doc.text(
    LEGEND.map((item) => `${item.code}=${item.label}`).join("  |  "),
    margin,
    y
  );
  y += 16;

  const headers = [
    "No",
    "Code",
    "Name",
    ...days.map((d) => d.label),
    ...TOTAL_CODES,
  ];
  const colCount = headers.length;
  const usableWidth = pageWidth - margin * 2;
  const nameWidth = 90;
  const noWidth = 22;
  const codeWidth = 36;
  const remaining = Math.max(
    usableWidth - noWidth - codeWidth - nameWidth,
    40
  );
  const dayWidth = remaining / Math.max(days.length + TOTAL_CODES.length, 1);
  const widths = [
    noWidth,
    codeWidth,
    nameWidth,
    ...Array(days.length + TOTAL_CODES.length).fill(dayWidth),
  ];

  const drawRow = (cells, isHeader = false) => {
    const rowHeight = 12;
    if (y + rowHeight > doc.internal.pageSize.getHeight() - margin) {
      doc.addPage();
      y = margin;
    }
    let x = margin;
    doc.setFontSize(isHeader ? 7 : 6.5);
    cells.forEach((cell, i) => {
      const w = widths[i] || dayWidth;
      doc.rect(x, y - 8, w, rowHeight);
      const text = String(cell ?? "");
      doc.text(text.slice(0, Math.max(1, Math.floor(w / 4))), x + 1, y);
      x += w;
    });
    y += rowHeight;
  };

  drawRow(headers, true);
  drawRow(["", "", "", ...days.map((d) => d.weekday || ""), ...TOTAL_CODES.map(() => "")], true);

  rows.forEach((row, index) => {
    const byDate = statusByDate(row);
    const totals = row.matrix_totals || {};
    drawRow([
      index + 1,
      row.person_code || "",
      row.full_name || "",
      ...days.map((d) => byDate[d.date] || "-"),
      ...TOTAL_CODES.map((c) => totals[c] ?? 0),
    ]);
  });

  doc.save(`${payload.filename || "Monthly_Basic_Attendance_Report"}.pdf`);
}

export { TOTAL_CODES, LEGEND };
